import { Fragment, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy, ExternalLink } from "lucide-react";
import { Sparkline } from "./sparkline";
import { usePrices } from "@/lib/prices-context";
import { HERO_ASSETS, type AssetEntry, STELLAR_EXPERT } from "@/lib/api";
import { formatPrice, formatAge, freshnessClass } from "@/lib/format";

/**
 * The category-first move: every asset on the page, with hover-reveal of the
 * raw signed payload. No other oracle shows you actual signed bytes in the
 * marketing site. Click any row → the 40-byte message, the 64-byte Ed25519
 * signature, and the publisher pubkey expand in place. The message is color-
 * segmented by field so you can see the protocol structure.
 */
export function AssetTerminal() {
  const { assets, history } = usePrices();
  const [active, setActive] = useState<string | null>(null);

  return (
    <section
      id="terminal"
      className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-12 sm:px-10 sm:pt-32 scroll-mt-12"
      aria-labelledby="terminal-title"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
        <span className="text-emerald">●</span> ASSET TERMINAL ·{" "}
        <span className="text-amber">TESTNET</span>
      </p>
      <h2
        id="terminal-title"
        className="mt-6 font-serif text-3xl leading-[1.05] tracking-[-0.012em] text-text sm:text-5xl md:text-6xl"
      >
        Every feed, <em className="italic">live</em>.
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-m70">
        Click any row to expand the raw signed payload — the 40-byte message,
        the 64-byte Ed25519 signature, and the publisher pubkey. This is the
        exact data your contract verifies on-chain.
      </p>

      <div className="mt-10 overflow-hidden rounded-xl border border-border bg-surface/40">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="border-b border-border bg-bg-deep/50">
                <Th>Asset</Th>
                <Th align="right">Price</Th>
                <Th align="right">Age</Th>
                <Th align="right">Trend</Th>
                <Th align="right">Sources</Th>
                <Th align="right" className="hidden sm:table-cell">
                  Round
                </Th>
                <Th align="right" className="w-8">
                  {/* expand affordance */}
                </Th>
              </tr>
            </thead>
            <tbody>
              {HERO_ASSETS.map((asset, i) => {
                const entry = assets?.[asset];
                const series = history[asset] ?? [];
                const isActive = active === asset;
                const isLast = i === HERO_ASSETS.length - 1;
                return (
                  <Fragment key={asset}>
                    <AssetRow
                      asset={asset}
                      entry={entry}
                      series={series}
                      isActive={isActive}
                      isLast={isLast && !isActive}
                      onToggle={() => setActive(isActive ? null : asset)}
                    />
                    <AnimatePresence initial={false}>
                      {isActive && entry && (
                        <SignedPayloadRow
                          key={`${asset}-panel`}
                          entry={entry}
                          isLast={isLast}
                        />
                      )}
                    </AnimatePresence>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------- table primitives ---------------------------- */

function Th({
  children,
  align = "left",
  className = "",
}: {
  children?: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <th
      className={`px-4 py-3 font-mono text-[9.5px] uppercase tracking-[0.22em] text-m40 font-medium ${
        align === "right" ? "text-right" : "text-left"
      } ${className}`}
    >
      {children}
    </th>
  );
}

function AssetRow({
  asset,
  entry,
  series,
  isActive,
  isLast,
  onToggle,
}: {
  asset: string;
  entry: AssetEntry | undefined;
  series: number[];
  isActive: boolean;
  isLast: boolean;
  onToggle: () => void;
}) {
  const [ageS, setAgeS] = useState<number | null>(null);

  // Tick the age field at ~5 Hz so it updates between rounds.
  useEffect(() => {
    if (!entry) {
      setAgeS(null);
      return;
    }
    const tick = () => setAgeS(Math.max(0, Date.now() / 1000 - entry.timestamp));
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [entry?.timestamp]);

  const direction = useMemo<"up" | "down" | "flat">(() => {
    if (series.length < 2) return "flat";
    const first = series[0];
    const last = series[series.length - 1];
    if (last > first) return "up";
    if (last < first) return "down";
    return "flat";
  }, [series]);

  const [symbol, quote] = asset.split("/");
  const priceText = entry ? formatPrice(asset, entry.price_human) : "—";
  const fresh = ageS != null ? freshnessClass(ageS) : "stale";
  const ageColor =
    fresh === "fresh"
      ? "text-emerald"
      : fresh === "warning"
      ? "text-amber"
      : "text-red";

  return (
    <tr
      onClick={onToggle}
      className={`group cursor-pointer border-b transition-colors ${
        isLast ? "border-transparent" : "border-border/60"
      } ${
        isActive
          ? "bg-surface-2/70"
          : "hover:bg-surface-2/40"
      }`}
    >
      <td className="px-4 py-4">
        <div className="font-mono text-[12px] uppercase tracking-[0.08em]">
          <span className="text-text font-semibold">{symbol}</span>
          <span className="text-m30">/{quote}</span>
        </div>
      </td>
      <td className="px-4 py-4 text-right font-mono text-[15px] tabular text-text">
        <span className="text-amber">$</span>
        {priceText.slice(1)}
      </td>
      <td
        className={`px-4 py-4 text-right font-mono text-[12px] tabular transition-colors ${ageColor}`}
      >
        {ageS == null ? "—" : formatAge(ageS)}
      </td>
      <td className="px-4 py-4 text-right">
        <div className="inline-block">
          <Sparkline
            points={series}
            direction={direction}
            width={72}
            height={20}
            fill
          />
        </div>
      </td>
      <td className="px-4 py-4 text-right font-mono text-[12px] tabular text-m70">
        {entry ? entry.sources : "—"}
      </td>
      <td className="hidden sm:table-cell px-4 py-4 text-right font-mono text-[11px] tabular text-m45">
        {entry ? entry.round_id.toLocaleString("en-US") : "—"}
      </td>
      <td className="px-4 py-4 text-right">
        <span
          className={`inline-block font-mono text-[10px] text-m45 transition-transform group-hover:text-emerald ${
            isActive ? "rotate-90 text-emerald" : ""
          }`}
        >
          ›
        </span>
      </td>
    </tr>
  );
}

/* ----------------------- signed-payload reveal row ----------------------- */

function SignedPayloadRow({
  entry,
  isLast,
}: {
  entry: AssetEntry;
  isLast: boolean;
}) {
  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className={isLast ? "" : "border-b border-border/60"}
    >
      <td colSpan={7} className="bg-bg-deep/70 p-0">
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          style={{ overflow: "hidden" }}
        >
          <PayloadPanel entry={entry} />
        </motion.div>
      </td>
    </motion.tr>
  );
}

function PayloadPanel({ entry }: { entry: AssetEntry }) {
  const { publisher, message, signature } = entry;
  // Split the 80-char message hex into the 4 protocol fields.
  const msgTag = message.slice(0, 16);
  const msgPrice = message.slice(16, 48);
  const msgTime = message.slice(48, 64);
  const msgRound = message.slice(64, 80);

  return (
    <div className="grid grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-5 lg:gap-10">
      {/* LEFT 3 cols: the bytes */}
      <div className="lg:col-span-3 space-y-5">
        <Field label="PUBLISHER · ED25519 PUBKEY">
          <CopyableHex value={publisher} />
        </Field>

        <Field
          label="SIGNED MESSAGE · 40 BYTES"
          hint={
            <span className="text-m45">
              <span className="text-emerald">▮</span> ASSET TAG ·{" "}
              <span className="text-amber">▮</span> PRICE I128 ·{" "}
              <span className="text-m70">▮</span> TIMESTAMP U64 ·{" "}
              <span className="text-emerald">▮</span> ROUND U64
            </span>
          }
        >
          <div className="font-mono text-[11.5px] leading-relaxed break-all rounded-md bg-bg/70 border border-border px-3 py-2.5">
            <span className="text-emerald">{msgTag}</span>
            <span className="text-amber">{msgPrice}</span>
            <span className="text-m70">{msgTime}</span>
            <span className="text-emerald">{msgRound}</span>
          </div>
        </Field>

        <Field label="ED25519 SIGNATURE · 64 BYTES">
          <CopyableHex value={signature} multiline />
        </Field>
      </div>

      {/* RIGHT 2 cols: verification context */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-md border border-emerald/30 bg-emerald/[0.04] p-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-emerald">
            <Check className="size-3.5" />
            VERIFIABLE ON-CHAIN
          </div>
          <p className="mt-2 text-[13px] leading-relaxed text-m70">
            This signature verifies inside Soroban via{" "}
            <code className="font-mono text-emerald">update_batch_ed25519_args</code>
            . Your contract then reads the just-verified price via{" "}
            <code className="font-mono text-text">get_price</code>.
          </p>
        </div>

        <DecodedField label="ASSET">{entry.tag}</DecodedField>
        <DecodedField label="PRICE (RAW i128)">{entry.price}</DecodedField>
        <DecodedField label="TIMESTAMP (UNIX)">
          {entry.timestamp.toLocaleString("en-US")} ·{" "}
          <span className="text-m45">
            {new Date(entry.timestamp * 1000).toISOString().replace("T", " ").slice(0, 19)}Z
          </span>
        </DecodedField>
        <DecodedField label="ROUND ID">
          {entry.round_id.toLocaleString("en-US")}
        </DecodedField>

        <div className="flex flex-wrap gap-2 pt-2">
          <CopyJsonButton entry={entry} />
          <a
            href={STELLAR_EXPERT.contract(
              "CAYIP67UDVX5UPXGN3XDAWVIEFBAVG6G7LUESEOU3NUQKTWN55W34YBG"
            )}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 rounded border border-border bg-surface/40 px-3 py-2 font-mono text-[9.5px] uppercase tracking-[0.22em] text-m70 transition-colors hover:border-emerald/40 hover:text-emerald"
          >
            <ExternalLink className="size-3" />
            Contract on Stellar.expert
          </a>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- field helpers ----------------------------- */

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-m40">
          {label}
        </span>
        {hint && (
          <span className="font-mono text-[9px] uppercase tracking-[0.22em]">
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function DecodedField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md bg-surface/40 border border-border px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-m40">
        {label}
      </div>
      <div className="mt-1 font-mono text-[12px] tabular text-text break-all">
        {children}
      </div>
    </div>
  );
}

function CopyableHex({ value, multiline }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* noop */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="group block w-full text-left rounded-md bg-bg/70 border border-border px-3 py-2.5 transition-colors hover:border-emerald/40"
      title="Click to copy"
    >
      <span
        className={`font-mono text-[11px] text-m70 ${
          multiline ? "break-all" : "block truncate"
        }`}
      >
        {value}
      </span>
      <span className="mt-1 inline-flex items-center gap-1 font-mono text-[8.5px] uppercase tracking-[0.24em] text-m40 group-hover:text-emerald transition-colors">
        {copied ? (
          <>
            <Check className="size-2.5" /> Copied
          </>
        ) : (
          <>
            <Copy className="size-2.5" /> Click to copy
          </>
        )}
      </span>
    </button>
  );
}

function CopyJsonButton({ entry }: { entry: AssetEntry }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* noop */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="inline-flex items-center gap-2 rounded border border-border bg-surface/40 px-3 py-2 font-mono text-[9.5px] uppercase tracking-[0.22em] text-m70 transition-colors hover:border-emerald/40 hover:text-emerald"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      {copied ? "Copied JSON" : "Copy as JSON"}
    </button>
  );
}
